/**
 * RuleResultCard (DC-1518, v3.0 Block 3).
 *
 * One deterministic rule outcome with its evidence, remediation, and
 * regulatory citations. The outcome badge owns the card's color channel;
 * severity is an uncolored word (two competing color systems on one card is
 * how dashboards turn into confetti). Badges are icon + label — never color
 * alone. Rule id + version are the audit citation: always visible, never
 * truncated.
 *
 * UX spec: docs/design/v3.0-domain-pack-surfaces-ux-spec.md §2.
 */

import React, { useState } from 'react';
import { Check, ChevronDown, ChevronRight, EyeOff, Minus, X } from 'lucide-react';
import type { SummaryEvidence, SummaryRuleResult } from './packJudgmentTypes';
import { isRuleOutcome } from './packJudgmentTypes';

// ---------------------------------------------------------------------------
// Outcome badge — the four-value vocabulary, exactly (spec §2.2 / mig 128)
// ---------------------------------------------------------------------------

const BADGES: Record<string, { label: string; Icon: React.ComponentType<{ size?: number | string; 'aria-hidden'?: boolean | 'true' | 'false' }> }> = {
  pass: { label: 'PASS', Icon: Check },
  fail: { label: 'FAIL', Icon: X },
  evidence_missing: { label: 'EVIDENCE MISSING', Icon: EyeOff },
  not_applicable: { label: 'NOT APPLICABLE', Icon: Minus },
};

export function OutcomeBadge({ outcome }: { outcome: string }): React.ReactElement {
  // Unknown outcomes render neutrally rather than crashing — the vocabulary
  // is DB-constrained, so this is a belt for future enum growth only.
  const key = isRuleOutcome(outcome) ? outcome : 'not_applicable';
  const { label, Icon } = BADGES[key];
  return (
    <span className={`dp-badge dp-badge--${key}`}>
      <Icon size={12} aria-hidden="true" />
      {isRuleOutcome(outcome) ? label : outcome.replace(/_/g, ' ').toUpperCase()}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Evidence rows (spec §2.1)
// ---------------------------------------------------------------------------

function formatObservation(observation: unknown): string {
  if (observation === null || observation === undefined) return '—';
  if (typeof observation === 'object') return JSON.stringify(observation);
  return String(observation);
}

function EvidenceRow({ evidence }: { evidence: SummaryEvidence }): React.ReactElement {
  // Full-confidence evidence doesn't need to say so; zero confidence is the
  // "couldn't see it" row behind evidence_missing — absence IS the finding,
  // so that observation alone carries warning ink.
  const absent = evidence.confidence === 0;
  return (
    <>
      <dt>{evidence.target}</dt>
      <dd className={absent ? 'dp-evidence--absent' : undefined}>
        {formatObservation(evidence.observation)}
        {evidence.confidence > 0 && evidence.confidence < 1 && (
          <span className="dp-evidence-conf"> · conf {evidence.confidence}</span>
        )}
      </dd>
    </>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

export interface RuleResultCardProps {
  result: SummaryRuleResult;
  /**
   * True when the judgment has > 4 rules (Hick's law — a 12-rule pack must
   * scan as a list first): evidence/remediation/citations start behind the
   * expander. With ≤ 4 rules the card renders fully expanded.
   */
  defaultCollapsed?: boolean;
}

export function RuleResultCard({ result, defaultCollapsed = false }: RuleResultCardProps): React.ReactElement {
  const [expanded, setExpanded] = useState(!defaultCollapsed);
  const hasBody =
    result.evidence.length > 0 ||
    result.remediation !== undefined ||
    result.regulatory_citations.length > 0 ||
    Boolean(result.description);

  const ariaLabel = `Rule ${result.rule_id}, version ${result.rule_version}, outcome ${result.outcome.replace(/_/g, ' ')}`;

  return (
    <article className="dp-card" aria-label={ariaLabel}>
      <div className="dp-card-header">
        <OutcomeBadge outcome={result.outcome} />
        <span className="dp-card-id">
          {result.rule_id} <span className="dp-card-version">· v{result.rule_version}</span>
        </span>
        {defaultCollapsed && hasBody && (
          <button
            type="button"
            className="dp-card-expander"
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
            {expanded ? 'Collapse' : 'Details'}
          </button>
        )}
        {result.severity && <span className="dp-card-severity">{result.severity}</span>}
      </div>

      {result.title && <p className="dp-card-title">{result.title}</p>}

      {expanded && hasBody && (
        <>
          {result.evidence.length > 0 && (
            <dl className="dp-evidence" aria-label={`Evidence for rule ${result.rule_id}`}>
              {result.evidence.map((e, idx) => (
                <EvidenceRow key={`${e.target}-${idx}`} evidence={e} />
              ))}
            </dl>
          )}

          {result.remediation && (
            <div className="dp-remediation">
              {result.remediation.action}
              <span className="dp-remediation-chips">
                <span className="dp-chip">EFFORT {result.remediation.estimated_effort.toUpperCase()}</span>
                <span className="dp-chip">VALUE {result.remediation.estimated_value.toUpperCase()}</span>
              </span>
            </div>
          )}

          {result.regulatory_citations.map((citation, idx) => (
            <p className="dp-citation" key={`${citation.framework}-${idx}`}>
              <span className="dp-citation-framework">{citation.framework}</span>{' '}
              <span className="dp-citation-reference">{citation.reference}</span>
              {citation.section && <span> · {citation.section}</span>}
            </p>
          ))}

          {result.description && (
            <details className="dp-card-why">
              <summary>Why this rule exists</summary>
              <p>{result.description}</p>
            </details>
          )}
        </>
      )}
    </article>
  );
}

export default RuleResultCard;
